import { Spinner, Typography } from '@weareopenr/component-library';
import { getPublishTime } from './getPublishTime/getPublishTime';
import CollapsibleSection from 'src/components/CollapsibleSection';
import { groupAndFormatSchedules } from 'src/pages/Mealtimes/groupAndFormatSchedules/groupAndFormatSchedules';
import { ISummaryViewProps } from './types';
import { IMealtime } from 'src/types/mealtimes.types';
import styles from './SummaryView.module.scss';
import { usePriceScheme } from 'src/hooks/usePriceScheme';
import { getVenuesWithChannels } from '../utilities/getVenuesWithChannels/getVenuesWithChannels';
import { formatDateTimeDayJs } from 'src/helpers/formatDateTime/formatDateTimeDayJs';

const SummaryView = ({
  menuName,
  watch,
  publicationData,
  filteredMealtimes,
  isMealtimeOptionEnabled,
}: ISummaryViewProps): React.ReactElement => {
  const { data: priceScheme, isLoading: isLoadingPriceScheme } = usePriceScheme(watch('price-scheme'));
  if (isLoadingPriceScheme) {
    return (
      <section className={styles['loading-container']}>
        <Spinner size="xlarge" colour="rainbow" />
      </section>
    );
  }

  const selectedChannelIds: string[] = watch('selectedChannels') ?? [];
  const selectedChannelSet = new Set(selectedChannelIds);
  const selectedChannels = publicationData.filter((channel) => selectedChannelSet.has(channel.channel_id));

  const selectedVenues = getVenuesWithChannels(publicationData, selectedChannelIds, watch('venues') ?? []);
  const numberOfVenues = selectedVenues.length;

  const formValues = watch();

  return (
    <section>
      <div className={styles['heading-space']}>
        <Typography size="xxlarge" className={styles['heading']}>
          Summary for <strong>{menuName}</strong>
        </Typography>
        <Typography size="medium">
          Menu snapshot to be published -{' '}
          <strong>
            {menuName} ({formatDateTimeDayJs(new Date())})
          </strong>
        </Typography>
      </div>
      <div className={styles['sections']}>
        <CollapsibleSection number="1" title="Mealtimes" subtitle={isMealtimeOptionEnabled ? 'Enabled' : 'Disabled'}>
          {isMealtimeOptionEnabled && !!filteredMealtimes.length && (
            <div className={styles['schedules']}>
              {filteredMealtimes.map((mealtime: IMealtime) => {
                const schedules = groupAndFormatSchedules(mealtime.schedules);
                return (
                  <div key={mealtime.name}>
                    <table>
                      <thead>
                        <tr>
                          <th className={styles['mealtime-name']}>
                            <Typography weight="semi-bold" transform="capitalize">
                              {mealtime.name}:
                            </Typography>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {schedules.map((schedule) => (
                          <tr key={`${schedule.startDay}-${schedule.endDay}-${schedule.time}`}>
                            <td className={styles['day']}>
                              <Typography>
                                {`${schedule.startDay}${schedule.endDay ? ' - ' + schedule.endDay : ''}:`}
                              </Typography>
                            </td>
                            <td>
                              <Typography>{schedule.time}</Typography>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          )}
        </CollapsibleSection>
        <CollapsibleSection number="2" title="Pricing" subtitle={priceScheme?.name} />
        <CollapsibleSection
          number="3"
          title="Channels"
          subtitle={`${selectedChannels.length} ${selectedChannels.length === 1 ? 'channel' : 'channels'} selected`}
        >
          <div className={styles['channel-container']}>
            <div className={styles['venue-container']}>
              {selectedChannels.map((channel) => (
                <div key={channel.channel_id}>
                  <Typography>{channel.name}</Typography>
                </div>
              ))}
            </div>
          </div>
        </CollapsibleSection>
        <CollapsibleSection
          number="4"
          title="Venues"
          subtitle={`${numberOfVenues} ${numberOfVenues === 1 ? 'venue' : 'venues'} selected`}
        >
          <div className={styles['channel-container']}>
            <div className={styles['venue-container']}>
              {selectedVenues.map(({ venue, channelNames }) => (
                <div key={venue.venue_id} data-testid={`summary-venue-${venue.venue_id}`}>
                  <Typography weight="semi-bold">{venue.name}</Typography>
                  <Typography size="small" className={styles['muted']}>
                    {channelNames.join(', ')}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        </CollapsibleSection>
        <CollapsibleSection
          number="5"
          title="Published date & time"
          subtitle={getPublishTime(formValues.publicationDate, formValues.publicationType)}
        />
      </div>
    </section>
  );
};

export default SummaryView;
